import React, { useState } from 'react';
import { getDatabase, ref, set, push, remove, update } from 'firebase/database';
import { useOperatingCostCategories } from '../../contexts/OperatingCostCategoriesContext';
import { OperatingCostCategory } from '../../types/operatingCosts';
import { Pencil, Trash2, Plus, X } from 'lucide-react';
import { recordActivity } from '../../api/activityApi';

type ModalState =
    | { mode: 'closed' }
    | { mode: 'add' }
    | { mode: 'edit'; category: OperatingCostCategory };

const OperatingCostCategoriesManagementPage: React.FC = () => {
    const { categories, loading, refresh } = useOperatingCostCategories();
    const [modalState, setModalState] = useState<ModalState>({ mode: 'closed' });
    const [modalLabel, setModalLabel] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    const db = getDatabase();
    const basePath = 'config/operatingCostCategories';

    const openAdd = () => {
        setModalLabel('');
        setModalState({ mode: 'add' });
    };

    const openEdit = (cat: OperatingCostCategory) => {
        setModalLabel(cat.label);
        setModalState({ mode: 'edit', category: cat });
    };

    const closeModal = () => {
        setModalState({ mode: 'closed' });
        setModalLabel('');
    };

    const handleSave = async () => {
        const label = modalLabel.trim();
        if (!label) return;

        setSaving(true);
        try {
            if (modalState.mode === 'add') {
                const newRef = push(ref(db, basePath));
                await set(newRef, { label });
                recordActivity('settings.updated', { targetKey: newRef.key ?? undefined, label });
            } else if (modalState.mode === 'edit') {
                await update(ref(db, `${basePath}/${modalState.category.key}`), { label });
                recordActivity('settings.updated', { targetKey: modalState.category.key, label });
            }
            closeModal();
            await refresh();
        } catch (err) {
            console.error('Error saving category:', err);
            setError(modalState.mode === 'add'
                ? 'Kunde inte lägga till kategorin.'
                : 'Kunde inte uppdatera kategorin.');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (key: string) => {
        try {
            await remove(ref(db, `${basePath}/${key}`));
            recordActivity('settings.updated', { targetKey: key });
            setDeleteConfirm(null);
            await refresh();
        } catch (err) {
            console.error('Error deleting category:', err);
            setError('Kunde inte ta bort kategorin.');
        }
    };

    if (loading) {
        return (
            <div className="text-center py-4">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Laddar...</span>
                </div>
            </div>
        );
    }

    return (
        <div style={{ maxWidth: 600 }}>
            <p className="text-muted mb-4">
                Hantera kategorier för driftkostnader (t.ex. el, värme, vatten, försäkring).
            </p>

            {error && (
                <div className="alert alert-danger alert-dismissible" role="alert">
                    {error}
                    <button type="button" className="btn-close" onClick={() => setError(null)} />
                </div>
            )}

            {/* Category list */}
            <div className="list-group mb-4">
                {categories.map((cat) => (
                    <div key={cat.key} className="list-group-item d-flex align-items-center gap-2">
                        {deleteConfirm === cat.key ? (
                            <>
                                <span className="flex-grow-1 text-danger" style={{ fontSize: '13px' }}>
                                    Kostnader med denna kategori blir okategoriserade. Ta bort?
                                </span>
                                <button
                                    className="btn btn-sm btn-danger"
                                    onClick={() => handleDelete(cat.key)}
                                >
                                    Ta bort
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setDeleteConfirm(null)}
                                >
                                    Avbryt
                                </button>
                            </>
                        ) : (
                            <>
                                <span className="flex-grow-1">{cat.label}</span>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => openEdit(cat)}
                                    title="Redigera"
                                >
                                    <Pencil size={14} />
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => setDeleteConfirm(cat.key)}
                                    title="Ta bort"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </>
                        )}
                    </div>
                ))}

                {categories.length === 0 && (
                    <div className="list-group-item text-muted text-center">
                        Inga kategorier skapade
                    </div>
                )}
            </div>

            {/* Add button */}
            <button
                className="btn btn-primary d-flex align-items-center gap-1"
                onClick={openAdd}
            >
                <Plus size={16} />
                Lägg till kategori
            </button>

            {/* Add / Edit modal */}
            {modalState.mode !== 'closed' && (
                <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog modal-dialog-centered modal-sm">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {modalState.mode === 'add' ? 'Ny kategori' : 'Redigera kategori'}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeModal} />
                            </div>
                            <form onSubmit={e => { e.preventDefault(); handleSave(); }}>
                                <div className="modal-body">
                                    <label htmlFor="categoryLabel" className="form-label">Namn</label>
                                    <input
                                        id="categoryLabel"
                                        type="text"
                                        className="form-control"
                                        value={modalLabel}
                                        onChange={e => setModalLabel(e.target.value)}
                                        placeholder="t.ex. Elektricitet"
                                        autoFocus
                                        required
                                    />
                                </div>
                                <div className="modal-footer">
                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={closeModal}
                                        disabled={saving}
                                    >
                                        <X className="me-1" size={14} />
                                        Avbryt
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={!modalLabel.trim() || saving}
                                    >
                                        {saving ? 'Sparar...' : modalState.mode === 'add' ? 'Lägg till' : 'Spara'}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default OperatingCostCategoriesManagementPage;
